import React, { useState, useEffect } from 'react';
import api, { unwrapList } from '../services/api';
import Table from '../components/common/Table';
import Modal from '../components/common/Modal';
import PageHeader from '../components/common/PageHeader';
import Badge from '../components/common/Badge';
import { formatDate, formatCurrency, formatDateTime } from '../utils/formatters';
import { EXPENSE_CATEGORIES } from '../utils/constants';
import { useVehicles } from '../hooks/useVehicles';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';

const todayIso = () => new Date().toISOString().split('T')[0];

export default function Expenses() {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [form, setForm]         = useState({ vehicle: '', category: 'fuel', amount: '', description: '', date: todayIso(), paid_by: 'owner' });
  const [saving, setSaving]     = useState(false);
  const { vehicles, selectedVehicle, handleDriverScopeError } = useVehicles();
  const { user } = useAuth();
  const isDriver = user?.role === 'driver';

  const load = async () => {
    setLoading(true);
    try {
      const params = isDriver ? undefined : { vehicle: selectedVehicle?.id };
      const res = await api.get('/expenses/', { params });
      const rows = unwrapList(res.data);
      setExpenses(rows);
    } catch (err) {
      if (isDriver) handleDriverScopeError(err);
      setExpenses([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [selectedVehicle, isDriver]);

  const handle = (e) => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, vehicle: form.vehicle || selectedVehicle?.id || '' };
      if (isDriver) payload.date = todayIso();
      await api.post('/expenses/', payload);
      toast.success('Expense recorded');
      setModal(false);
      setForm(p => ({ ...p, vehicle: selectedVehicle?.id || '', amount: '', description: '', date: todayIso() }));
      load();
    } catch (err) {
      if (isDriver && handleDriverScopeError(err)) {
        return;
      }
      toast.error('Failed to record expense. Select a vehicle and try again.');
    } finally { setSaving(false); }
  };

  const columns = [
    { key: 'date', label: 'Date', render: (v) => formatDate(v) },
    { key: 'vehicle_reg', label: 'Vehicle' },
    { key: 'category', label: 'Category', render: (v) => EXPENSE_CATEGORIES.find(c => c.value === v)?.label || v },
    { key: 'description', label: 'Description', render: (v) => v || '—' },
    { key: 'paid_by', label: 'Paid By', render: (v) => <Badge status={v === 'owner' ? 'info' : 'neutral'} label={v === 'owner' ? 'Owner' : 'Driver'} /> },
    { key: 'amount', label: 'Amount', render: (v) => <span className="mono-number">{formatCurrency(v)}</span>, tdStyle: { textAlign: 'right' } },
  ];

  return (
    <div>
      <PageHeader
        title="Expenses"
        subtitle={isDriver ? 'Today only: expense records (click a row for details)' : 'All vehicle expenses (click a row for details)'}
        actions={<button className="btn btn-primary" onClick={() => setModal(true)}>+ Add Expense</button>}
      />
      <div className="card">
        <Table
          columns={columns}
          data={expenses}
          loading={loading}
          emptyMessage="No expenses recorded"
          onRowClick={(row) => setSelectedExpense(row)}
        />
      </div>

      <Modal isOpen={Boolean(selectedExpense)} onClose={() => setSelectedExpense(null)} title="Expense Details">
        {selectedExpense && (
          <div style={{ display: 'grid', gap: '0.8rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
              <span style={{ color: 'var(--gray-500)' }}>Amount</span>
              <strong className="mono-number">{formatCurrency(selectedExpense.amount)}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
              <span style={{ color: 'var(--gray-500)' }}>Category</span>
              <strong>{EXPENSE_CATEGORIES.find(c => c.value === selectedExpense.category)?.label || selectedExpense.category}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
              <span style={{ color: 'var(--gray-500)' }}>Paid By</span>
              <Badge
                status={selectedExpense.paid_by === 'owner' ? 'info' : 'neutral'}
                label={selectedExpense.paid_by === 'owner' ? 'Owner' : 'Driver'}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
              <span style={{ color: 'var(--gray-500)' }}>Vehicle</span>
              <strong>{selectedExpense.vehicle_reg || '—'}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
              <span style={{ color: 'var(--gray-500)' }}>Expense Date</span>
              <strong>{formatDate(selectedExpense.date)}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
              <span style={{ color: 'var(--gray-500)' }}>Recorded At</span>
              <strong>{selectedExpense.created_at ? formatDateTime(selectedExpense.created_at) : '—'}</strong>
            </div>
            <div style={{ borderTop: '1px solid var(--gray-100)', paddingTop: '0.7rem' }}>
              <div style={{ color: 'var(--gray-500)', marginBottom: '0.25rem' }}>Description</div>
              <div>{selectedExpense.description || 'No description provided.'}</div>
            </div>
          </div>
        )}
      </Modal>

      <Modal isOpen={modal} onClose={() => setModal(false)} title="Record Expense">
        <form onSubmit={submit}>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Vehicle *</label>
              <select className="form-control" name="vehicle" value={form.vehicle || selectedVehicle?.id} onChange={handle} required>
                <option value="">Select vehicle</option>
                {vehicles.map(v => <option key={v.id} value={v.id}>{v.registration_number}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Date *</label>
              <input
                className="form-control"
                type="date"
                name="date"
                value={isDriver ? todayIso() : form.date}
                onChange={handle}
                required
                disabled={isDriver}
              />
            </div>
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Category *</label>
              <select className="form-control" name="category" value={form.category} onChange={handle}>
                {EXPENSE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Paid By</label>
              <select className="form-control" name="paid_by" value={form.paid_by} onChange={handle}>
                <option value="owner">Owner</option>
                <option value="driver">Driver</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Amount (KES) *</label>
            <input className="form-control" type="number" name="amount" value={form.amount} onChange={handle} placeholder="0" required min="0" style={{ fontFamily: 'var(--font-mono)' }} />
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <input className="form-control" name="description" value={form.description} onChange={handle} placeholder="Optional note" />
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save Expense'}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}